import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Market } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteMarket } from '../hooks/use-market-mutations';
import { marketChangeErrorKey } from '../schemas/market-rules';

export interface DeleteMarketDialogProps {
  storeId: string;
  market: Market | null;
  onOpenChange: (open: boolean) => void;
}

/** Confirms deletion; the default market answers 409 and the dialog says why. */
export function DeleteMarketDialog({ storeId, market, onOpenChange }: DeleteMarketDialogProps) {
  const { t } = useTranslation(['markets', 'common', 'errors']);
  const remove = useDeleteMarket(storeId);
  const message = useErrorMessage();
  const errorKey = remove.error ? marketChangeErrorKey(remove.error) : null;
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={market !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('markets:delete.title', { name: market?.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('markets:delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {errorKey ? t(errorKey) : message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (market) remove.mutate(market.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {remove.isPending ? t('markets:delete.submitting') : t('markets:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
